import { Modal } from "@/Common/Components/Modals/Modal";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { QuebecObstetricalFileClickableTextContext } from "@/Common/Models/UI/ClickableTextContext";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { WeightUnit } from "@/Common/Utilities/Units/Units";
import { IObstetricalFilePageProps } from "@/Patient/Components/MedicalNote/Obstetric/Form/ObstetricalFilePage";
import { QuebecObstetricalFilePage, QuebecObstetricalFilePageState } from "@/Patient/Components/MedicalNote/Obstetric/Form/Quebec/QuebecObstetricalFilePage";
import { BloodPressureField } from "@/Patient/Components/MedicalNote/ObstetricalFile/Control/BloodPressureField";
import { GestationalAgeComponent } from "@/Patient/Components/MedicalNote/ObstetricalFile/Control/GestationalAgeComponent";
import { ObstetricalCounter } from "@/Patient/Components/MedicalNote/ObstetricalFile/Control/ObstetricalCountersComponent";
import { ObstetricalFileControlRenderer } from "@/Patient/Components/MedicalNote/ObstetricalFile/ControlRenderer/ObstetricalFileControlRenderer";
import { QuebecObstetricalFileFieldLimits } from "@/Patient/Components/MedicalNote/ObstetricalFile/Form/Quebec/QuebecObstetricalFileFieldLimits";
import { MedicalCalculator } from "@/Patient/Helpers/MedicalCalculator";
import { ObstetricsCalculator } from "@/Patient/Helpers/ObstetricsCalculator";
import { BloodPressure } from "@/Patient/Models/MedicalSummary/BloodPressure";
import { ObstetricalFileVitalSign } from "@/Patient/Models/ObstetricalFile/ObstetricalFileVitalSign";
import { PrenatalVisitInitials } from "@/Patient/Models/ObstetricalFile/PrenatalVisitInitials";
import { EstimatedDateOfConfinementMethod } from "@/Patient/Models/ObstetricalFile/Quebec/PageFour/EstimatedDateOfConfinementMethodEnum";
import { PrenatalVisit } from "@/Patient/Models/ObstetricalFile/Quebec/PageFour/PrenatalVisit";
import { QuebecObstetricalFileFormPageFour } from "@/Patient/Models/ObstetricalFile/Quebec/PageFour/QuebecObstetricalFileFormPageFour";
import { QuebecObstetricalFileForm } from "@/Patient/Models/ObstetricalFile/Quebec/QuebecObstetricalFileForm";

class QuebecObstetricalFilePageFourState extends QuebecObstetricalFilePageState {
    public pageModel: QuebecObstetricalFileFormPageFour;
    public prenatalVisitRows: PrenatalVisit[];
    public currentEditPrenatalRowId: number;
    public isVitalSignOptionPopupShown: boolean;
    public heightInMeters: number;
    public prenatalVistsTableWidthMultiplier: number = 1;
}

class QuebecObstetricalFilePageFour extends QuebecObstetricalFilePage<QuebecObstetricalFilePageFourState> {
    private readonly componentId = "obstetrical-file-page-four";
    private _vitalSignControlRenderer: ObstetricalFileControlRenderer;

    constructor(props: IObstetricalFilePageProps<QuebecObstetricalFileForm>) {
        super(props);
        this.setVitalSignControlRenderer(props.readonly);
    }

    public componentDidMount(): void {
        let prenatalVisit = this.state.prenatalVisitRows.firstOrDefault(p => p.ContributionId === this.props.contributionId);
        if (!prenatalVisit || !prenatalVisit.VitalSignEmrTemplateId)
            return;

        this.handleObstetricalFileVitalSignLink(prenatalVisit.VitalSignEmrTemplateId);
    }

    public componentDidUpdate(prevProps: IObstetricalFilePageProps<QuebecObstetricalFileForm>): void {
        super.componentDidUpdate(prevProps);
        if (this.props.readonly !== prevProps.readonly) {
            this.mutateState(s => s.isVitalSignOptionPopupShown = this.shouldVitalSignOptionPopupShow(this.props, s));
        }
    }

    protected get vitalSignControlRenderer(): ObstetricalFileControlRenderer {
        if (this._vitalSignControlRenderer?.getIsReadonly() !== this.props.readonly) {
            this.setVitalSignControlRenderer(this.props.readonly);
        }
        return this._vitalSignControlRenderer;
    }

    private setVitalSignControlRenderer(readonly: boolean): void {
        this._vitalSignControlRenderer = new ObstetricalFileControlRenderer({
            readonly: readonly,
            onBlur: () => this.onVitalSignUpdated()
        });
    }

    protected buildState(props: IObstetricalFilePageProps<QuebecObstetricalFileForm>): QuebecObstetricalFilePageFourState {
        const state = new QuebecObstetricalFilePageFourState();
        state.form = new QuebecObstetricalFileForm(props.form);
        state.pageModel = state.form.PageFour;
        state.prenatalVisitRows = state.form.PageFour?.PrenatalVisits;

        state.isVitalSignOptionPopupShown = this.shouldVitalSignOptionPopupShow(props, state);

        state.form.setPrenatalVisitVitalSigns(
            props.obstetricalFileMedicalSummary
            , props.encounter.ConsultationDate
            , props.contributionCreationDate
            , props.contributionId
        );

        state.heightInMeters = ObstetricalFileVitalSign.createObstetricalFileVitalSign({
            obstetricalFileMedicalSummary: props.obstetricalFileMedicalSummary,
            emrTemplateId: state.form.PageTwo.VitalSignEmrTemplateId,
            consultationDate: props.encounter.ConsultationDate,
            obstetricalFileContributionDate: props.contributionCreationDate,
            skipLoadLastHour: true,
            weightUnit: state.form.PageTwo.WeightUnit,
            heightUnit: state.form.PageTwo.HeightUnit
        })?.heightInMeters;

        return state;
    }

    protected renderPageContent(): JSX.Element {
        return (
            <div id={this.componentId}>
                {this.renderGTPALSection()}
                {this.renderPrenatalVisitsSection()}

                {this.state.isVitalSignOptionPopupShown &&
                    this.renderVitalSignOptionPopup()
                }
            </div>
        );
    }

    private renderVitalSignOptionPopup(): JSX.Element {
        let footer = (
            <div className="modal-footer">
                <button
                    className="btn btn-default"
                    onClick={() => this.closeReuseVitalSignPopup(null, () => this.handleFormUpdate())}
                >
                    {cacheStore.GetString("NewVitalSignsLabel")}
                </button>
                <button
                    className="btn btn-primary"
                    onClick={() => this.onReuseVitalSign()}
                >
                    {cacheStore.GetString("ReuseLabel")}
                </button>
            </div>
        );

        return (
            <Modal
                title={cacheStore.GetString("ObstetricalFile_ReusingVitalSignsTitleLabel")}
                showOverlay
                disableOverlayClose
                roundTopCorner
                footer={footer}
                onClose={() => this.mutateState(s => s.isVitalSignOptionPopupShown = false)}
            >
                <div>
                    {cacheStore.GetString("ObstetricalFile_ReusingVitalSignsQuestionLabel")}
                </div>
            </Modal>
        );
    }

    private renderGTPALSection(): JSX.Element {
        const particularitiesId = "obstetrical-counter-particularities";
        const particularities = this.state.pageModel.Particularities;

        return (
            <div className="section border border-top">
                <div className="row">
                    {this.renderObstetricalCounters("col-xs-7", true, true, [ObstetricalCounter.Gravida, ObstetricalCounter.Term, ObstetricalCounter.Premature, ObstetricalCounter.Abortion, ObstetricalCounter.Live])}
                </div>
                <div className="row">
                    <div className="col-xs-12">
                        <span className="field-label">{cacheStore.GetString("ParticularitiesLabel")}</span>
                        {this.controlRenderer.renderTextarea({
                            value: particularities,
                            onChange: value => this.mutateState(s => s.pageModel.Particularities = value),
                            id: particularitiesId,
                            maxLength: QuebecObstetricalFileFieldLimits.Page4.Particularities.MaxChars,
                            maxRows: QuebecObstetricalFileFieldLimits.Page4.Particularities.MaxRows,
                            clickableTextOptions: this.getClickableTextOptions(particularitiesId, QuebecObstetricalFileClickableTextContext.PageFour_Particularities)
                        })}
                    </div>
                </div>
            </div>
        );
    }

    private renderPrenatalVisitsSection(): JSX.Element {
        return (
            <div className="section border border-top prenatal-visits">
                <div className="row header">
                    <div className="col-xs-12">
                        <h4>{cacheStore.GetString("PrenatalVisitsLabel")}</h4>
                    </div>
                </div>

                <div className="row">
                    <div className="col-xs-12 weight-before-pregnancy">
                        <span className="field-label-inline">{cacheStore.GetString("WeightBeforePregnancyLabel")}</span>
                        <div className="field-value-inline textbox unit-container">
                            {this.controlRenderer.renderAutoCompleteTextbox({
                                value: this.state.form.WeightBeforePregnancy?.toString(),
                                autoCompleteItems: this.getWeightBeforePregnancyDropdownOptions(this.state.form.WeightBeforePregnancyUnit),
                                onChange: value => this.handleWeightBeforePregnancyChange(value),
                                onAutoCompleteSelected: code => this.handleWeightBeforePregnancyChange(code),
                                maxLength: QuebecObstetricalFileFieldLimits.Page4.PrenatalVisitsWeightBeforePregnancy.MaxChars,
                                isNumeric: true,
                                disableMatchAutoSorting: true
                            })}
                            {this.controlRenderer.renderWeightUnitSelect(
                                this.state.form.WeightBeforePregnancyUnit,
                                value => this.mutateState(s => s.form.setWeightBeforePregnancyUnit(value)),
                                "unit"
                            )}
                        </div>
                    </div>
                </div>

                <div className="row">
                    {this.renderPrenatalVisitsTable()}
                    {this.renderAddNewVisitLink()}
                </div>
            </div>
        );
    }

    private renderAddNewVisitLink(): JSX.Element {
        if (this.props.readonly || this.contributionHasPrenatalVisitRow())
            return null;

        return (
            <div className="col-xs-12">
                <a onClick={() => this.addPrenatalVisitRow()}>{`[+] ${cacheStore.GetString("AddNewVisitLabel")}`}</a>
            </div>
        );
    }

    private renderPrenatalVisitsTable(): JSX.Element {
        return (
            <div className="col-xs-12">
                <table className="prenatal-visit-table">
                    {this.renderPrenatalVisitsTableHeader()}
                    {this.renderPrenatalVisitsTableBody()}
                </table>
            </div>
        );
    }

    private renderPrenatalVisitsTableHeader(): JSX.Element {
        return (
            <thead>
                <tr className="header-empty">
                    <td colSpan={5} />
                    <td colSpan={2}><div className="floating-urine"><strong>{cacheStore.GetString("UrineLabel")}</strong></div></td>
                    <td colSpan={7} />
                </tr>
                <tr className="header">
                    {this.renderPrenatalVisitsHeaderCell("", "date")}
                    {this.renderPrenatalVisitsHeaderCell("WeeksOfGestationLabel")}
                    {this.renderPrenatalVisitsHeaderCell("HeightOfUterineFundusLabel")}
                    {this.renderPrenatalVisitsHeaderCell("WeightLabel", "weight")}
                    {this.renderPrenatalVisitsHeaderCell("BloodPressureLabel")}
                    {this.renderPrenatalVisitsHeaderCell("AlbuminuriaLabel")}
                    {this.renderPrenatalVisitsHeaderCell("GlycosuriaLabel")}
                    {this.renderPrenatalVisitsHeaderCell("FetalMovementLabel")}
                    {this.renderPrenatalVisitsHeaderCell("ContractionsLabel")}
                    {this.renderPrenatalVisitsHeaderCell("FetalHeartLabel")}
                    {this.renderPrenatalVisitsHeaderCell("PresentationLabel")}
                    {this.renderPrenatalVisitsHeaderCell("EdemaLabel")}
                    <td colSpan={2}>
                        {this.renderPrenatalVisitsEstimatedDateOfConfinementAndRemarks()}
                    </td>
                </tr>
                <tr className="sub-header">
                    <td className="text-center"><span>{cacheStore.GetString("DateLabel")}</span></td>
                    <td />
                    <td />
                    <td>{this.renderWeightUnit(this.state.pageModel.WeightUnit, unit => this.handleOnWeightUnitChange(unit))}</td>
                    <td />
                    <td />
                    <td />
                    <td />
                    <td />
                    <td />
                    <td />
                    <td />
                    <td colSpan={2} className="treatment-remarks-label"><strong>{cacheStore.GetString("TreatmentOrRemarksLabel")}</strong></td>
                </tr>
            </thead>
        );
    }

    private renderPrenatalVisitsHeaderCell(text: string = "", className: string = ""): JSX.Element {
        return (
            <td>
                <div className="header-col">
                    <div className={`tilted ${className}`}>
                        <div className="text">
                            {text && cacheStore.GetString(text)}
                        </div>
                    </div>
                </div>
            </td>
        );
    }

    private renderPrenatalVisitsEstimatedDateOfConfinementAndRemarks(): JSX.Element {
        const dateFieldWidthInPixels = 115;
        const remarksId = "remarks";

        return (
            <div className="edc-remark">
                <div className="edc">
                    <strong>{cacheStore.GetString("EstimatedDateOfConfinementAccordingToAbbrevLabel")}</strong>
                    <div className="header-col">
                        <div className="flex-column">
                            <div className="flex-row">
                                {this.renderEstimatedDateOfConfinementMethodSelector(EstimatedDateOfConfinementMethod.DateOfLastMenstruation, cacheStore.GetString("DateOfLastMenstrualPeriodAcronymLabel"))}

                                {this.controlRenderer.renderDateSelector({
                                    value: this.state.pageModel.EstimatedDateOfConfinementDateOfLastMenstruation,
                                    onChange: value => this.mutateState(
                                        s => {
                                            s.pageModel.EstimatedDateOfConfinementDateOfLastMenstruation = value;

                                            if (EnumHelper.getValue(EstimatedDateOfConfinementMethod, s.pageModel.EstimatedDateOfConfinementMethod) === EstimatedDateOfConfinementMethod.DateOfLastMenstruation)
                                                s.pageModel.updateWeeksOfGestation(this.props.contributionId);
                                        },
                                        () => this.handleFormUpdate()
                                    ),
                                    id: "EstimatedDateOfConfinementDateOfLastMenstruation",
                                    className: "estimated-date-confinement",
                                    width: dateFieldWidthInPixels
                                })}

                            </div>

                            <div className="flex-row">

                                {this.renderEstimatedDateOfConfinementMethodSelector(EstimatedDateOfConfinementMethod.ClinicDate, cacheStore.GetString("ClinicLabel"))}

                                {this.controlRenderer.renderDateSelector({
                                    value: this.state.pageModel.EstimatedDateOfConfinementClinicDate,
                                    onChange: value => this.mutateState(
                                        s => {
                                            s.pageModel.EstimatedDateOfConfinementClinicDate = value;

                                            if (EnumHelper.getValue(EstimatedDateOfConfinementMethod, s.pageModel.EstimatedDateOfConfinementMethod) === EstimatedDateOfConfinementMethod.ClinicDate)
                                                s.pageModel.updateWeeksOfGestation(this.props.contributionId);
                                        },
                                        () => this.handleFormUpdate()
                                    ),
                                    id: "EstimatedDateOfConfinementClinicDate",
                                    className: "estimated-date-confinement",
                                    width: dateFieldWidthInPixels
                                })}

                            </div>

                            <div className="flex-row">

                                {this.renderEstimatedDateOfConfinementMethodSelector(EstimatedDateOfConfinementMethod.Ultrasound20WeeksDate, cacheStore.GetString("UltrasoundBefore20WeeksAbbrev"))}

                                {this.controlRenderer.renderDateSelector({
                                    value: this.state.pageModel.EstimatedDateOfConfinementUltrasound20WeeksDate,
                                    onChange: value => this.mutateState(
                                        s => {
                                            s.pageModel.EstimatedDateOfConfinementUltrasound20WeeksDate = value;

                                            if (EnumHelper.getValue(EstimatedDateOfConfinementMethod, s.pageModel.EstimatedDateOfConfinementMethod) === EstimatedDateOfConfinementMethod.Ultrasound20WeeksDate)
                                                s.pageModel.updateWeeksOfGestation(this.props.contributionId);
                                        },
                                        () => this.handleFormUpdate()
                                    ),
                                    id: "EstimatedDateOfConfinementUltrasound20WeeksDate",
                                    className: "estimated-date-confinement",
                                    width: dateFieldWidthInPixels
                                })}

                            </div>

                            <div className="flex-row">

                                {this.renderEstimatedDateOfConfinementMethodSelector(EstimatedDateOfConfinementMethod.OtherUltrasound, cacheStore.GetString("OtherUltrasoundLabel"))}

                                {this.controlRenderer.renderDateSelector({
                                    value: this.state.pageModel.EstimatedDateOfConfinementOtherUltrasound,
                                    onChange: value => this.mutateState(
                                        s => {
                                            s.pageModel.EstimatedDateOfConfinementOtherUltrasound = value;

                                            if (EnumHelper.getValue(EstimatedDateOfConfinementMethod, s.pageModel.EstimatedDateOfConfinementMethod) === EstimatedDateOfConfinementMethod.OtherUltrasound)
                                                s.pageModel.updateWeeksOfGestation(this.props.contributionId);
                                        },
                                        () => this.handleFormUpdate()
                                    ),
                                    id: "EstimatedDateOfConfinementOtherUltrasound",
                                    className: "estimated-date-confinement",
                                    width: dateFieldWidthInPixels
                                })}

                            </div>
                        </div>

                    </div>
                </div>
                <div className="remark">
                    <strong>{cacheStore.GetString("RemarksLabel")}</strong>
                    <div className="header-col">
                        {this.controlRenderer.renderTextarea({
                            value: this.state.pageModel.Remarks,
                            onChange: value => this.mutateState(s => s.pageModel.Remarks = value),
                            id: remarksId,
                            maxLength: QuebecObstetricalFileFieldLimits.Page4.EstimatedDateOfConfinementRemarks.MaxChars,
                            maxRows: QuebecObstetricalFileFieldLimits.Page4.EstimatedDateOfConfinementRemarks.MaxRows,
                            clickableTextOptions: this.getClickableTextOptions(remarksId, QuebecObstetricalFileClickableTextContext.PageFour_Remarks)
                        })}
                    </div>
                </div>
            </div>
        );
    }

    private renderEstimatedDateOfConfinementMethodSelector(estimationMethod: EstimatedDateOfConfinementMethod, label: string): JSX.Element {
        return (
            <div className="flex-row radio-option">
                <input
                    type="radio"
                    id={label}
                    name="edc-methods"
                    disabled={this.props.readonly}
                    checked={this.state.pageModel.EstimatedDateOfConfinementMethod === estimationMethod}
                    onClick={() =>
                        this.mutateState(
                            s => {
                                s.pageModel.EstimatedDateOfConfinementMethod = estimationMethod;
                                s.pageModel.updateWeeksOfGestation(this.props.contributionId);
                            },
                            () => this.handleFormUpdate()
                        )
                    }
                />
                <label htmlFor={label}>{label}</label>
            </div>
        );
    }

    private renderPrenatalVisitsTableBody(): JSX.Element {
        return (
            <tbody>
                {this.state.prenatalVisitRows?.map((p, idx) => this.renderPrenatalVisitRows(p, idx))}
            </tbody>
        );
    }

    private renderPrenatalVisitRows(visit: PrenatalVisit, index: number): JSX.Element[] {
        if (!visit)
            return;

        const visitKey = visit.Id.toString();
        const isLatestMedicalItemInEmrTemplate = this.props.obstetricalFileMedicalSummary.isLatestMedicalItemInEmrTemplate(visit.VitalSign);
        const treatmentOrRemarksId = "treatmentOrRemarks_" + visitKey;

        let extraTemplateClassName = visit.VitalSign.emrIsInvalid ? "invalid-vital-sign" : "";
        if (!isLatestMedicalItemInEmrTemplate)
            extraTemplateClassName += " not-allowed-hover";

        let clickableTextOptions = null;
        if (index === this.state.prenatalVisitRows.length - 1) {
            clickableTextOptions = this.getClickableTextOptions(treatmentOrRemarksId, QuebecObstetricalFileClickableTextContext.PageFour_TreatmentOrRemarks);
            clickableTextOptions.selectFromListCssClass = "visit-select-from-list";
        }

        const firstRow = (
            <tr className="content-row">
                <td>
                    <div className="visit-date large">
                        {this.controlRenderer.renderDateSelector({
                            value: visit.Date,
                            onChange: value => this.mutatePrenatalVisitRowState(
                                visit.Id,
                                p => {
                                    setTimeout(() => {
                                        p.Date = value;
                                        const expectedDueDate = this.state.pageModel.getChosenMethodEDCDate();
                                        if (!!expectedDueDate)
                                            p.WeeksOfGestation = ObstetricsCalculator.calculateWeeksOfGestation(expectedDueDate, value);
                                    }, 0);
                                },
                                () => this.handleFormUpdate()
                            ),
                            id: `visitDate_${visitKey}`,
                            className: "table-date-selector",
                            width: 113
                        })}
                    </div>
                </td>
                <td>
                    <div className="medium">
                        <GestationalAgeComponent
                            id={`WeeksOfGestation_${visitKey}`}
                            gestationalAge={visit.WeeksOfGestation}
                            readonly={this.props.readonly}
                            onChange={value => this.mutatePrenatalVisitRowState(visit.Id, p => p.WeeksOfGestation = value)}
                            controlRenderer={this.controlRenderer}
                            weekClassName="prenatal-visit-week-gestational-age"
                            dayClassName="prenatal-visit-day-gestational-age "
                        />
                    </div>
                </td>
                <td>
                    <div className="x-small">
                        {this.controlRenderer.renderTextbox({
                            value: visit.HeightOfUterineFundus,
                            onChange: value => this.mutatePrenatalVisitRowState(visit.Id, p => p.HeightOfUterineFundus = value),
                            className: "table-textbox",
                            maxLength: QuebecObstetricalFileFieldLimits.Page4.PrenatalVisitsHeightOfUterineFundus.MaxChars
                        })}
                    </div>
                </td>
                <td>
                    <div className="medium">
                        {this.vitalSignControlRenderer.renderTextbox({
                            value: visit.Weight?.toString(),
                            onChange: value => this.mutatePrenatalVisitRowState(visit.Id, p => p.Weight = value && Number(value)),
                            className: `table-textbox ${extraTemplateClassName}`,
                            isReadOnlyOverride: !isLatestMedicalItemInEmrTemplate,
                            isNumeric: true,
                            maxLength: QuebecObstetricalFileFieldLimits.Page4.PrenatalVisitsWeight.MaxChars
                        })}
                    </div>
                </td>
                <td>
                    <div className="small">
                        <BloodPressureField
                            cssClassName="margin-auto"
                            cssClassNameInput={`blood-pressure ${extraTemplateClassName}`}
                            readonly={this.props.readonly || !isLatestMedicalItemInEmrTemplate}
                            bloodPressure={visit.VitalSign?.bloodPressure}
                            isInvalid={visit.VitalSign.emrIsInvalid}
                            onBlur={() => this.onVitalSignUpdated()}
                            onBloodPressureChange={(bloodPressure: BloodPressure) => this.mutatePrenatalVisitRowState(visit.Id, p => p.VitalSign.bloodPressure = bloodPressure)}
                        />
                    </div>
                </td>
                <td>
                    <div className="small">
                        {this.controlRenderer.renderTextbox({
                            value: visit.Albuminuria,
                            onChange: value => this.mutatePrenatalVisitRowState(visit.Id, p => p.Albuminuria = value),
                            className: "table-textbox",
                            maxLength: QuebecObstetricalFileFieldLimits.Page4.PrenatalVisitsAlbuminuria.MaxChars
                        })}
                    </div>
                </td>
                <td>
                    <div className="small">
                        {this.controlRenderer.renderTextbox({
                            value: visit.Glycosuria,
                            onChange: value => this.mutatePrenatalVisitRowState(visit.Id, p => p.Glycosuria = value),
                            className: "table-textbox",
                            maxLength: QuebecObstetricalFileFieldLimits.Page4.PrenatalVisitsGlycosuria.MaxChars
                        })}
                    </div>
                </td>
                <td>
                    <div className="small">
                        {this.controlRenderer.renderTextbox({
                            value: visit.FetalMovement,
                            onChange: value => this.mutatePrenatalVisitRowState(visit.Id, p => p.FetalMovement = value),
                            className: "table-textbox",
                            maxLength: QuebecObstetricalFileFieldLimits.Page4.PrenatalVisitsFetalMovement.MaxChars
                        })}
                    </div>
                </td>
                <td>
                    <div className="small">
                        {this.controlRenderer.renderTextbox({
                            value: visit.Contractions,
                            onChange: value => this.mutatePrenatalVisitRowState(visit.Id, p => p.Contractions = value),
                            className: "table-textbox",
                            maxLength: QuebecObstetricalFileFieldLimits.Page4.PrenatalVisitsContractions.MaxChars
                        })}
                    </div>
                </td>
                <td>
                    <div className="small">
                        {this.controlRenderer.renderTextbox({
                            value: visit.FetalHeart,
                            onChange: value => this.mutatePrenatalVisitRowState(visit.Id, p => p.FetalHeart = value),
                            className: "table-textbox",
                            maxLength: QuebecObstetricalFileFieldLimits.Page4.PrenatalVisitsFetalHeart.MaxChars
                        })}
                    </div>
                </td>
                <td>
                    <div className="x-small">
                        {this.controlRenderer.renderAutoCompleteTextbox({
                            value: visit.Presentation,
                            autoCompleteItems: ["∂", "9", "б", "ϱ", "C", cacheStore.GetString("BreechAbbrevLabel"), "T"].map(p => new SearchableItemModelT<string>(p, p, p)),
                            onChange: value => this.mutatePrenatalVisitRowState(visit.Id, p => p.Presentation = value),
                            onAutoCompleteSelected: value => this.mutatePrenatalVisitRowState(visit.Id, p => p.Presentation = value),
                            className: "table-textbox",
                            disableMatchAutoSorting: true,
                            maxLength: 1
                        })}
                    </div>
                </td>
                <td>
                    <div className="small">
                        {this.controlRenderer.renderTextbox({
                            value: visit.Edema,
                            onChange: value => this.mutatePrenatalVisitRowState(visit.Id, p => p.Edema = value),
                            className: "table-textbox",
                            maxLength: QuebecObstetricalFileFieldLimits.Page4.PrenatalVisitsEdema.MaxChars
                        })}
                    </div>
                </td>

                <td className="treatment-remark" rowSpan={2}>
                    <div className="expandable-col">
                        {this.controlRenderer.renderResizableTextarea({
                            value: visit.TreatmentOrRemarks,
                            onChange: value => this.mutatePrenatalVisitRowState(visit.Id, p => p.TreatmentOrRemarks = value),
                            id: treatmentOrRemarksId,
                            maxLength: 5000,
                            minHeight: "80px",
                            maxHeight: "500px",
                            className: "table-textarea",
                            containerCssClass: "resizable",
                            clickableTextOptions: clickableTextOptions
                        })}
                    </div>
                </td>
                <td className="signature" rowSpan={2}>
                    <div className="expandable-col">
                        {this.controlRenderer.renderResizableTextarea({
                            value: visit.Initials?.Value,
                            onChange: value => this.mutatePrenatalVisitRowState(visit.Id, p => p.Initials.Value = value, null, true),
                            id: `signature_${visitKey}`,
                            maxLength: 5000,
                            minHeight: "35px",
                            maxHeight: "500px",
                            className: "table-textarea"
                        })}
                    </div>
                </td>
            </tr>
        );

        const descriptionRow = (
            <tr>
                <td className="description" colSpan={12}>
                    <div>
                        {this.controlRenderer.renderTextbox({
                            value: visit.Description,
                            onChange: value => this.mutatePrenatalVisitRowState(visit.Id, p => p.Description = value),
                            className: "table-textbox visit-description-field",
                            containerClassName: "description-row",
                            maxLength: QuebecObstetricalFileFieldLimits.Page4.PrenatalVisitsExtraInformation.MaxChars
                        })}
                    </div>
                </td>
            </tr>
        );

        return [firstRow, descriptionRow];
    }

    private closeReuseVitalSignPopup(mutation?: (state: QuebecObstetricalFilePageFourState) => void, then?: () => void): void {
        this.mutateState(
            s => {
                s.isVitalSignOptionPopupShown = false;
                s.pageModel.IsReuseVitalSignOptionSet = true;
                mutation?.(s);
            },
            then
        );
    }

    private onReuseVitalSign(): void {
        this.closeReuseVitalSignPopup(s => {
            if (!s.prenatalVisitRows.any())
                return;

            s.currentEditPrenatalRowId = s.prenatalVisitRows[0].Id;
            s.prenatalVisitRows[0].VitalSignEmrTemplateId = s.form.PageTwo.VitalSignEmrTemplateId;
            s.prenatalVisitRows[0].VitalSign = ObstetricalFileVitalSign.createObstetricalFileVitalSign({
                obstetricalFileMedicalSummary: this.props.obstetricalFileMedicalSummary,
                emrTemplateId: s.form.PageTwo.VitalSignEmrTemplateId,
                consultationDate: this.props.encounter.ConsultationDate,
                obstetricalFileContributionDate: this.props.contributionCreationDate,
                skipLoadLastHour: false,
                weightUnit: s.pageModel.WeightUnit
            });
            s.pageModel.PrenatalVisits = s.prenatalVisitRows;
        }, () => this.onVitalSignUpdated());
    }

    private mutatePrenatalVisitRowState(id: number, mutation: (state: PrenatalVisit) => void, then?: () => void, isInitialsChange?: boolean): void {
        this.mutateState(
            s => {
                s.prenatalVisitRows = this.state.prenatalVisitRows.mutate(
                    p => {
                        mutation(p);

                        if (isInitialsChange)
                            p.Initials.tryAddUserContactFacetId(sessionStore.getCurrentEmployee().ContactFacetId);
                        else
                            p.Initials.addInitialsFromUser(sessionStore.getCurrentEmployee(), this.existingInitials, " ");
                    },
                    p => p.Id === id
                );
                s.currentEditPrenatalRowId = id;
                s.pageModel.PrenatalVisits = s.prenatalVisitRows;
            },
            () => then?.()
        );
    }

    private async onVitalSignUpdated(): Promise<void> {
        if (IsNullOrUndefined(this.state.currentEditPrenatalRowId))
            return;

        let prenantalVisit = this.state.prenatalVisitRows.firstOrDefault(p => p.Id === this.state.currentEditPrenatalRowId);
        const updatedVitalSign = await super.handleVitalSignUpdate(
            prenantalVisit?.VitalSign,
            prenantalVisit?.VitalSignEmrTemplateId
        );

        this.mutatePrenatalVisitRowState(
            this.state.currentEditPrenatalRowId,
            s => {
                s.updateVitalSignValue(updatedVitalSign, this.state.pageModel.WeightUnit);
                s.VitalSignEmrTemplateId = updatedVitalSign?.EMRTemplateId;
            },
            () => this.handleObstetricalFileVitalSignLink(updatedVitalSign?.EMRTemplateId)
        );
    }

    private shouldVitalSignOptionPopupShow(props: IObstetricalFilePageProps<QuebecObstetricalFileForm>, state: QuebecObstetricalFilePageFourState): boolean {
        const vitalSign = props.obstetricalFileMedicalSummary.getVitalSignBy(state.form.PageTwo.VitalSignEmrTemplateId, props.encounter.ConsultationDate);

        return !state.pageModel.IsReuseVitalSignOptionSet
            && !props.readonly
            && state.form.PageTwo.VitalSignEmrTemplateId
            && vitalSign
            && (vitalSign.Weight || vitalSign.BPDia || vitalSign.BPSys)
            && LuxonDateTimeParser.parse(vitalSign.PharmaStartDate).hasSame(LuxonDateTimeParser.parse(props.encounter.ConsultationDate), "day")
            && (state.form.PageFour.PrenatalVisits?.any() !== true || !state.form.PageFour.PrenatalVisits[0].VitalSignEmrTemplateId);
    }

    private contributionHasPrenatalVisitRow(): boolean {
        return this.state.prenatalVisitRows.any(p => p.ContributionId === this.props.contributionId);
    }

    private handleOnWeightUnitChange(unit: WeightUnit): void {
        this.mutateState(
            s => {
                s.pageModel.setWeightUnit(unit);
                s.prenatalVisitRows.forEach(p => p.WeightUnit = unit);
            },
            () => this.handleFormUpdate()
        );
    }

    private handleWeightBeforePregnancyChange(value: any): void {
        if (isNaN(value))
            return;

        this.mutateState(s => {
            s.form.WeightBeforePregnancy = value;
            s.form.PrePregnancyBodyMassIndex = Number(MedicalCalculator.calculateBodyMassIndex(s.form.WeightBeforePregnancyInKg, s.heightInMeters)?.toFixed(2));
        });
    }

    private get existingInitials(): PrenatalVisitInitials[] {
        return this.state.pageModel.PrenatalVisits.map(v => v.Initials);
    }

    private addPrenatalVisitRow(): void {
        this.mutateState(
            s => {
                let newPrenatalVisit = new PrenatalVisit({
                    ContributionId: this.props.contributionId,
                    Date: this.props.encounter.ConsultationDate,
                    WeeksOfGestation: ObstetricsCalculator.calculateWeeksOfGestation(s.pageModel.getChosenMethodEDCDate(), this.props.encounter.ConsultationDate)
                });

                const vitalSign = ObstetricalFileVitalSign.createObstetricalFileVitalSign({
                    obstetricalFileMedicalSummary: this.props.obstetricalFileMedicalSummary,
                    consultationDate: this.props.encounter.ConsultationDate,
                    obstetricalFileContributionDate: this.props.contributionCreationDate,
                    skipLoadLastHour: false,
                    weightUnit: s.pageModel.WeightUnit
                });

                newPrenatalVisit.VitalSign = vitalSign;
                newPrenatalVisit.VitalSignEmrTemplateId = vitalSign?.emrTemplateId;

                s.prenatalVisitRows = [...s.prenatalVisitRows, newPrenatalVisit];
                s.pageModel.PrenatalVisits = s.prenatalVisitRows;
            },
            () => {
                let prenatalVisit = this.state.prenatalVisitRows.firstOrDefault(p => p.ContributionId === this.props.contributionId);
                if (!prenatalVisit || !prenatalVisit.VitalSignEmrTemplateId) {
                    this.handleFormUpdate();
                    return;
                }

                this.handleObstetricalFileVitalSignLink(prenatalVisit.VitalSignEmrTemplateId);
            }
        );
    }
}

export { QuebecObstetricalFilePageFour };
